import type IInputChangeHandler from '../IInputChangeHandler';
import ControlType from './ControlType';

export type FormControlElement = HTMLInputElement | HTMLSelectElement;

export default abstract class FormControl<T extends FormControlElement> {
  abstract readonly type: ControlType;

  $inputs: T[];
  changeHandlers = [] as IInputChangeHandler[];

  constructor($inputs: T[]) {
    this.$inputs = $inputs;
  }

  addChangeHandler(handler: IInputChangeHandler) {
    this.changeHandlers.push(handler);
    handler.start();
  }

  startListening() {
    this.changeHandlers.forEach((ch) => ch.start());
  }

  stopListening() {
    this.changeHandlers.forEach((ch) => ch.stop());
  }

  get isRequired(): boolean {
    return this.$inputs[0].hasAttribute('required');
  }

  set isRequired(value: boolean) {
    this.$inputs.forEach(($input) => {
      if (value) {
        $input.setAttribute('required', 'required');
      } else {
        $input.removeAttribute('required');
      }
    });
  }

  get isReadonly(): boolean {
    return this.$inputs[0].getAttribute('readonly') === 'true';
  }

  set isReadonly(value: boolean) {
    this.$inputs.forEach(($input) => {
      if (value) {
        $input.setAttribute('readonly', 'readonly');
      } else {
        $input.removeAttribute('readonly');
      }
    });
  }
  
  get isDisabled(): boolean {
    return this.$inputs[0].getAttribute('disabled') === 'true';
  }
  
  set isDisabled(value: boolean) {
    this.$inputs.forEach(($input) => {
      if (value) {
        $input.setAttribute('disabled', 'true');
      }
      else {
        $input.removeAttribute('disabled');
      }
    })
  }

  get isInvalid(): boolean {
    return this.$inputs.some(input => input.getAttribute('aria-invalid') === 'true');
  }

  set isInvalid(value: boolean) {
    const attr = 'aria-invalid';
    this.$inputs.forEach(input => {
        if (value) {
            input.setAttribute(attr, 'true');
        } else {
            input.removeAttribute(attr);
        } 
    });
  }

  get name(): string {
    return this.$inputs[0].name;
  }

  get value(): string {
    return this.$inputs[0].value;
  }

  set value(value: string) {
    if (this.$inputs[0].value !== value) {
      this.$inputs[0].value = value;
      this.$inputs[0].dispatchEvent(new Event('change'));
    }
  }

  setValueSilent(value: string) {
    this.$inputs[0].value = value;
  }

  get elementId() {
    return this.$inputs[0].id;
  }

  get label() {
    let $controlLabel: HTMLElement | undefined | null = null;

    if (this.type === ControlType.Radio || this.type === ControlType.Checkbox) {
      $controlLabel = this.$inputs[0].closest('.form-group')?.querySelector('.control-label');
    } else {
      $controlLabel = this.$inputs[0].labels?.item(0);
    }

    if ($controlLabel) {
      const labelText = $controlLabel.innerText;

      const $requiredIndicator = $controlLabel.querySelector<HTMLSpanElement>('.tn-required-field');

      if ($requiredIndicator) {
        const requiredIndicatorText = $requiredIndicator.innerText;

        if (labelText.endsWith(requiredIndicatorText)) {
          return labelText.slice(0, labelText.length - requiredIndicatorText.length).trim();
        }
      }

      return labelText;
    }

    const $fieldset = this.$inputs[0].closest('fieldset');
    const fallbackLabel = $fieldset?.getAttribute('data-label');
    return fallbackLabel?.trim() || undefined;
  }

  appendToAttribute(attributeName: string, value: string) {
    for (let i = 0; i < this.$inputs.length; i++) {
      const attribute = this.$inputs[i].attributes.getNamedItem(attributeName);
      
      if (attribute === null) {
        this.$inputs[i].setAttribute(attributeName, value);
      } else {
        this.appendToInputAttribute(attribute, value);
      }
    }
  }
  
  dropFromAttribute(attributeName: string, value: string) {
    for (let i = 0; i < this.$inputs.length; i++) {
      const attribute = this.$inputs[i].attributes.getNamedItem(attributeName);
      
      if (attribute === null) {
        continue;
      }
      
      if (attribute.value === '' || attribute.value === value) {
        this.$inputs[i].removeAttribute(attributeName);
        continue;
      }
      
      this.dropFromInputAttribute(attribute, value);
    }
  }

  private appendToInputAttribute(attribute: Attr, newValue: string) {
    if (attribute.value === '') {
      attribute.value = newValue;
      return;
    }

    const existingValues = attribute.value.split(' ');

    if (existingValues.includes(newValue)) {
      return;
    }

    existingValues.push(newValue);
    attribute.value = existingValues.join(' ');
  }
  
  private dropFromInputAttribute(attribute: Attr, value: string) {    
    let existingValues = attribute.value.split(' ');
    const targetValueIndex = existingValues.findIndex(attrValue => attrValue === value);
    
    if (targetValueIndex === -1) {
      return;
    }
    
    existingValues.splice(targetValueIndex, 1);
    attribute.value = existingValues.join(' ');
  }
}
